import { AccountAccount, CircleCircle } from '@calm/evexia-web-sdk';
import useLogout from '@rhg/hooks/use-logout';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { isAxiosError } from 'axios';
import useAccount from '../use-account';

const circlesQueryOptions = {
    queryKey: () => [queryKeys.circles],
    queryFn: async ({
        getAccessTokenSilently,
        rHGApp,
        forceRefreshSponsor = false,
        logout,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        rHGApp: string;
        forceRefreshSponsor?: boolean;
        logout: () => Promise<void>;
    }) => {
        try {
            const api = createCircleApi(getAccessTokenSilently);
            const response = await api.getCirclesV1({
                rHGApp,
                forceRefreshSponsor: forceRefreshSponsor ? 'true' : undefined,
            });
            const circles = response.data;

            circles.forEach((circle) =>
                queryClient.setQueryData<CircleCircle>(
                    [queryKeys.circleById, circle.recipient.id],
                    circle
                )
            );

            return circles;
        } catch (error) {
            if (isAxiosError(error) && error?.response?.status === 401) {
                logout();
            }
            throw error;
        }
    },
    queryOptions: ({
        isAuthenticated,
        account,
    }: {
        isAuthenticated: boolean;
        account?: AccountAccount;
    }) => ({
        enabled: !!(isAuthenticated && account?.userId),
        staleTime: 1000 * 60 * 60 * 8, // 8 hours
    }),
};

const useCircles = () => {
    const { queryKey, queryFn, queryOptions } = circlesQueryOptions;
    const { logout } = useLogout();
    const rHGApp = getRhgAppHeader();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { account } = useAccount();
    const { data, ...otherProps } = useQuery({
        queryKey: queryKey(),
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
                rHGApp,
                logout,
            }),
        ...queryOptions({ isAuthenticated, account }),
    });

    return {
        circles: data,
        hasCircles: data ? data?.length > 0 : false,
        ...otherProps,
    };
};

export function useGetCircles() {
    const { queryFn } = circlesQueryOptions;
    const { logout } = useLogout();
    const rHGApp = getRhgAppHeader();
    const { getAccessTokenSilently } = useAuth();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: (forceRefreshSponsor: boolean = false) =>
            queryFn({
                forceRefreshSponsor,
                rHGApp,
                logout,
                getAccessTokenSilently,
            }),
    });
    return { getCircles: mutate, getCirclesAsync: mutateAsync, ...otherProps };
}

export default useCircles;
